import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowSquareOut,
  Brain,
  CheckCircle,
  Cloud,
  FilmStrip,
  MagicWand,
  NewspaperClipping,
  Scissors,
  VideoCamera,
  type Icon,
} from '@phosphor-icons/react';

import Xiaozhushou_src from '~/assets/images/xiaozhushou.jpg';
import XiaozhushouV_src from '~/assets/videos/xiaozhushou.mp4';

import './index.less';

const SPOTLIGHT_FEATURES = [
  {
    icon: Brain,
    title: '智能自动化',
    desc: '把剪映里的手动操作，交给自动化流程完成',
  },
  {
    icon: VideoCamera,
    title: '智能视频生成',
    desc: '图转视频 / 图生视频，AI 生成动态内容',
  },
  {
    icon: Scissors,
    title: '专业剪辑',
    desc: '视频拼接、裁剪与字幕特效一次配置',
  },
  {
    icon: FilmStrip,
    title: '专业编辑',
    desc: '多轨配音与音频处理，成片直接进入草稿',
  },
] as const;

interface ProductCard {
  id: string;
  kicker: string;
  title: string;
  desc: string;
  icon: Icon;
  accent: 'blue' | 'violet' | 'teal';
  features: { title: string; desc: string }[];
  tags: string[];
  action: { label: string; to?: string; href?: string };
}

const PRODUCTS: ProductCard[] = [
  {
    id: 'video-studio',
    kicker: '创作中心',
    title: '智能视频创作中心',
    desc: '把生成式 AI 接入视频生产，从灵感、素材到成片，一条链路完成。',
    icon: MagicWand,
    accent: 'blue',
    features: [
      { title: '多平台支持', desc: '扣子国内 / 海外平台' },
      { title: '智能创作', desc: '图转视频 / 图生视频' },
      { title: '专业编辑', desc: '拼接、裁剪、字幕特效' },
      { title: '应用场景', desc: '短视频 / 营销广告 / 教育培训' },
    ],
    tags: ['扣子插件定制', 'MCP 插件定制'],
    action: { label: '浏览工作流', to: '/workflow' },
  },
  {
    id: 'scene-split',
    kicker: '镜头工具',
    title: '视频镜头分割大师',
    desc: '自动识别场景切换点，把长视频拆成可直接剪辑的镜头素材。',
    icon: Scissors,
    accent: 'violet',
    features: [
      { title: '智能场景检测', desc: '自动识别视频中的场景切换点' },
      { title: '灵活配置', desc: '自定义场景检测灵敏度' },
      { title: 'RESTful API', desc: '简洁易用的 HTTP 接口' },
      { title: '容器化部署', desc: '支持 Docker 一键部署' },
    ],
    tags: ['添加转场', '打乱镜头', '添加蒙版'],
    action: { label: '查看效果演示', href: 'https://b23.tv/GtCIJAG' },
  },
  {
    id: 'media-ops',
    kicker: '内容运营',
    title: '全媒体内容运营助手',
    desc: '从图文生成到多平台分发，一站式完成内容生产与运营。',
    icon: NewspaperClipping,
    accent: 'teal',
    features: [
      { title: '公众号运营插件', desc: 'AI 文章智能生成与优化' },
      { title: '一键推送', desc: '同步至微信公众平台' },
      { title: '多资源嵌入', desc: '图文 / 视频 / 小程序' },
    ],
    tags: ['图文编辑工具', '一键同步多平台'],
    action: { label: '浏览工作流', to: '/workflow' },
  },
];

const CapabilitySection = () => {
  return (
    <section className="home-capabilities" id="capabilities">
      <div className="container">
        <header className="home-section-head">
          <span className="home-section-eyebrow">产品能力</span>
          <h2 className="home-section-title">一条链路，覆盖 AI 视频全流程</h2>
          <p className="home-section-desc">
            从素材生成、镜头处理到剪辑成片，把分散的工具收进同一个平台。
          </p>
        </header>

        <article className="home-spotlight" id="jianying">
          <div className="home-spotlight__content">
            <span className="home-spotlight__eyebrow">剪映生态 · 旗舰产品</span>
            <h3 className="home-spotlight__title">剪映小助手</h3>
            <p className="home-spotlight__desc">
              一键将 AI
              生成的视频内容导入剪映，草稿、配音、字幕自动生成；与本地剪映无缝衔接，让创作从素材到成片全流程自动化。
            </p>

            <ul className="home-spotlight__features">
              {SPOTLIGHT_FEATURES.map(({ icon: FeatureIcon, title, desc }) => (
                <li className="home-spotlight__feature" key={title}>
                  <span className="home-spotlight__feature-icon">
                    <FeatureIcon size={20} weight="fill" aria-hidden />
                  </span>
                  <div className="home-spotlight__feature-text">
                    <strong>{title}</strong>
                    <span>{desc}</span>
                  </div>
                </li>
              ))}
            </ul>

            <div className="home-spotlight__actions">
              <Link to="/download" className="home-btn home-btn--primary">
                前往下载
                <ArrowRight size={18} weight="bold" aria-hidden />
              </Link>
              <span className="home-spotlight__note">支持 Windows 8.1+ / macOS 12.0+</span>
            </div>
          </div>

          <div className="home-spotlight__media">
            <div className="home-spotlight__video-wrap">
              <video
                className="home-spotlight__video"
                muted
                autoPlay
                loop
                playsInline
                preload="metadata"
                poster={Xiaozhushou_src}
                aria-label="剪映小助手功能演示"
              >
                <source src={XiaozhushouV_src} type="video/mp4" />
                您的浏览器不支持 HTML5 video 标签。
              </video>
              <span className="home-spotlight__caption">
                <Cloud size={15} weight="fill" aria-hidden />
                草稿自动生成 · 云端渲染
              </span>
            </div>
          </div>
        </article>

        <div className="home-product-grid">
          {PRODUCTS.map((product) => {
            const ProductIcon = product.icon;
            return (
              <article
                className={`home-product-card home-product-card--${product.accent}`}
                id={product.id}
                key={product.id}
              >
                <div className="home-product-card__top">
                  <span className="home-product-card__icon">
                    <ProductIcon size={24} weight="fill" aria-hidden />
                  </span>
                  <span className="home-product-card__kicker">{product.kicker}</span>
                </div>
                <h3 className="home-product-card__title">{product.title}</h3>
                <p className="home-product-card__desc">{product.desc}</p>

                <ul className="home-product-card__features">
                  {product.features.map((feature) => (
                    <li key={feature.title}>
                      <CheckCircle size={16} weight="fill" aria-hidden />
                      <span className="home-product-card__feature-text">
                        <strong>{feature.title}</strong>
                        <span className="home-product-card__feature-desc">{feature.desc}</span>
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="home-product-card__tags">
                  {product.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>

                <footer className="home-product-card__footer">
                  {product.action.to ? (
                    <Link className="home-product-card__link" to={product.action.to}>
                      {product.action.label}
                      <ArrowRight size={16} weight="bold" aria-hidden />
                    </Link>
                  ) : (
                    <a
                      className="home-product-card__link"
                      href={product.action.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {product.action.label}
                      <ArrowSquareOut size={16} weight="bold" aria-hidden />
                    </a>
                  )}
                </footer>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CapabilitySection;
